import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  Zap, Users, TrendingUp, Clock, Loader2, Save, BarChart3,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const ENDPOINT_LABELS = {
  draft: "AI Draft",
  suggest_clauses: "Clause Suggestions",
  generate_template: "Template Generation",
  contextual_review: "Contextual Review",
  risk_review: "Risk Review",
  extract_deliverables_kpis: "Extract Deliverables/KPIs",
  extract_obligations: "Extract Obligations",
  extract_obligations_event: "Extract from Event",
  executive_summary: "Executive Summary",
  draft_clause: "Clause Drafting",
  clause_benchmark: "Clause Benchmark",
  decision_analyze: "Decision Intelligence",
  supplier_analyze: "Supplier Analysis",
  notice_draft: "Notice Drafting",
};

export function AIUsageTab() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [perUser, setPerUser] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLimit, setEditingLimit] = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  const isAdmin = user?.role === "superuser" || user?.role === "admin";

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, usersRes] = await Promise.all([
        apiClient.get("/ai/usage/dashboard"),
        apiClient.get("/ai/usage/users"),
      ]);
      setDashboard(dashRes.data);
      setPerUser(usersRes.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) fetchData(); }, [fetchData, isAdmin]);

  const sortedEndpoints = useMemo(() => {
    if (!dashboard?.by_endpoint) return [];
    return Object.entries(dashboard.by_endpoint).sort((a, b) => b[1] - a[1]);
  }, [dashboard]);

  const handleSaveLimit = async (userId) => {
    const limit = parseInt(newLimit);
    if (isNaN(limit) || limit < 0 || limit > 10000) { toast.error("Limit must be 0–10,000"); return; }
    setSavingLimit(true);
    try {
      await apiClient.put(`/users/${userId}/ai-limit`, { daily_limit: limit });
      toast.success(`Daily limit updated to ${limit}`);
      setEditingLimit(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSavingLimit(false); }
  };

  if (!isAdmin) {
    return <p className="text-sm text-slate-400 py-8 text-center">Admin access required to view AI usage.</p>;
  }

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={24} /></div>;
  }

  const d = dashboard || {};
  const quotaPct = d.monthly_pct || 0;
  const barColor = quotaPct >= 95 ? "bg-red-500" : quotaPct >= 80 ? "bg-amber-500" : "bg-cyan-500";

  return (
    <div className="space-y-6" data-testid="ai-usage-tab">
      {/* Quota Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-cyan-100 flex items-center justify-center">
                <Zap size={18} className="text-cyan-600" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold">{d.monthly_used?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">of {d.monthly_quota?.toLocaleString() || 0} monthly quota</p>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1.5">
                  <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(quotaPct, 100)}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">{quotaPct}% used — {d.plan} plan</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-emerald-100 flex items-center justify-center">
                <TrendingUp size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{d.today_total || 0}</p>
                <p className="text-xs text-slate-500">AI requests today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-purple-100 flex items-center justify-center">
                <Users size={18} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{perUser.length}</p>
                <p className="text-xs text-slate-500">Active AI users this month</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage by Endpoint */}
      {d.by_endpoint && Object.keys(d.by_endpoint).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <BarChart3 size={14} /> Usage by Feature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sortedEndpoints.map(([key, count]) => {
                const pct = d.monthly_used > 0 ? (count / d.monthly_used * 100) : 0;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-40 truncate">{ENDPOINT_LABELS[key] || key}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2">
                      <div className="bg-cyan-400 h-2 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-500 w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Trend */}
      {d.daily_trend && d.daily_trend.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Clock size={14} /> 14-Day Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-24">
              {d.daily_trend.map((day, i) => {
                const maxCount = Math.max(...d.daily_trend.map(t => t.count), 1);
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${day.date}: ${day.count} requests`}>
                    <div className="w-full bg-cyan-400 rounded-t-sm transition-all" style={{ height: `${Math.max(height, 2)}%` }} />
                    {i % 2 === 0 && <span className="text-[8px] text-slate-400">{day.date.slice(5)}</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-User Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <Users size={14} /> Per-User AI Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          {perUser.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No AI usage recorded this month yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-usage-users-table">
                <thead>
                  <tr className="border-b text-left text-xs font-mono uppercase tracking-wider text-slate-400">
                    <th className="pb-2 pr-4">User</th>
                    <th className="pb-2 pr-4">Role</th>
                    <th className="pb-2 pr-4">Today</th>
                    <th className="pb-2 pr-4">This Month</th>
                    <th className="pb-2 pr-4">Daily Limit</th>
                    <th className="pb-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {perUser.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`ai-user-row-${u.user_id}`}>
                      <td className="py-2.5 pr-4">
                        <p className="font-medium text-sm">{u.name || "Unnamed"}</p>
                        <p className="text-[10px] text-slate-400">{u.email}</p>
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge className="rounded-sm text-[10px]">{u.role}</Badge>
                      </td>
                      <td className="py-2.5 pr-4 font-mono">{u.today_count}</td>
                      <td className="py-2.5 pr-4 font-mono font-medium">{u.monthly_count}</td>
                      <td className="py-2.5 pr-4">
                        {editingLimit === u.user_id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={newLimit}
                              onChange={e => setNewLimit(e.target.value)}
                              className="h-7 w-20 rounded-sm text-xs"
                              data-testid={`ai-limit-input-${u.user_id}`}
                            />
                            <Button size="sm" className="h-7 rounded-sm px-2" onClick={() => handleSaveLimit(u.user_id)} disabled={savingLimit} data-testid={`ai-limit-save-${u.user_id}`}>
                              {savingLimit ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 rounded-sm px-2" onClick={() => setEditingLimit(null)}>X</Button>
                          </div>
                        ) : (
                          <span className="font-mono text-slate-600 cursor-pointer hover:text-cyan-600" onClick={() => { setEditingLimit(u.user_id); setNewLimit(String(u.daily_limit)); }} data-testid={`ai-limit-display-${u.user_id}`}>
                            {u.daily_limit}/day
                          </span>
                        )}
                      </td>
                      <td className="py-2.5">
                        {u.today_count >= u.daily_limit && (
                          <Badge className="bg-red-100 text-red-700 rounded-sm text-[9px]">Limit hit</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
